// @flow

import React, {Component} from 'react'
import '../App.css';
import * as d3 from 'd3';
import Utils from "../Utils";
import {Network} from './Models';
import Slider from '@material-ui/lab/Slider';
import Colors from "../Colors";
import {RNG} from "../RNG";

type Props = {
  randomSeed?: number,
  reinfectionsAllowed?: number,

  // Network
  degree?: number,
  immunityFraction?: number,
  networkType?: string,
  networkSize?: number,

  // Simulation parameters
  maxIterations?: number,
  transmissionProbability?: number,

  // Rendering parameters
  drawNodeOutlines?: boolean,
  drawRelationships?: boolean,
  height?: number,
  nodeSize?: number,
  width?: number,

  // Controls
  numAutoRuns?: number,
  showDrawRelationshipsCheckbox?: boolean,
  showImmunityFractionSlider?: boolean,
  showSimulationButtons?: boolean,
  showTransmissionProbabilitySlider?: boolean,
}

type State = {
  playing: boolean,

  // Network
  degree: number,
  immunityFraction: number,
  networkType: string,
  networkSize: number,

  // Simulation
  maxIterations: number,
  transmissionProbability: number,

  // Rendering
  drawNodeOutlines: boolean,
  drawRelationships: boolean,
  nodeSize: number,
}

export default class Widget extends Component<Props, State> {
  // Set default props
  static defaultProps = {
    randomSeed: -1,
    reinfectionsAllowed: false,

    // Network
    degree: 4,
    immunityFraction: 0,
    networkType: 'grid',
    networkSize: 361,

    // Simulation parameters
    maxIterations: -1,
    transmissionProbability: 0.3,

    // Rendering parameters
    drawNodeOutlines: true,
    drawRelationships: true,
    height: 650,
    nodeSize: 20,
    width: 650,

    // Controls
    showDrawRelationshipsCheckbox: false,
    showImmunityFractionSlider: false,
    showSimulationButtons: false,
    showTransmissionProbabilitySlider: false,
  };

  network: Network;
  rng: RNG;

  interval: any;  // timer for animations

  constructor(props: Props) {
    super(props);

    this.myRef = React.createRef();

    this.previousSimulationParams = [];
    this.network = null;

    this.previousDrawingParams = [];

    this.initializeFromProps(this.props, true);
  }

  componentWillReceiveProps(nextProps: Props, nextContext: any): void {
    this.initializeFromProps(nextProps, false);
  }

  initializeFromProps(props: Props, fromConstructor: boolean) {
    this.width = props.width;
    this.height = props.height;

    let randomSeed = props.randomSeed;
    if (randomSeed === -1) {
      randomSeed = Math.floor(Math.random() * 3000000);
    }
    this.rng = new RNG(randomSeed);

    let state = {
      playing: false,

      // Network
      degree: props.degree,
      immunityFraction: props.immunityFraction,
      networkSize: props.networkSize,
      networkType: props.networkType,

      // Simulation
      maxIterations: props.maxIterations,
      transmissionProbability: props.transmissionProbability,

      // Rendering
      drawNodeOutlines: props.drawNodeOutlines,
      drawRelationships: props.drawRelationships,
      nodeSize: props.nodeSize,
    };
    if (fromConstructor) {
      this.state = state;
    } else {
      this.setState(state);
    }

    if (props.reinfectionsAllowed) {
      this.onTick = this.onTick.bind(this);
      this.interval = setInterval(this.onTick, 100);
    }
  }

  componentDidMount() {
    this.svg = d3.select(this.myRef.current);

    this.svg.on("click", () => {
      // let mouse = d3.mouse(this.myRef.current);
      // let x = mouse[0];
      // let y = mouse[1];
    });

    this.regenerate();
  }

  onTick() {
    if (this.state.playing) {
      this.simulateStep();
      this.redraw(true);
    }
  }

  generate(force: boolean) {
    // actually regenerate iff any of the simulation parameters have changed
    let currentSimulationParams = [
        this.state.degree,
        this.state.immunityFraction,
        this.state.networkSize,
        this.state.networkType,
    ];
    if (!force && Utils.arraysEqual(this.previousSimulationParams, currentSimulationParams)) {
      console.log('rejecting generate');
      return;
    }

    this.previousSimulationParams = currentSimulationParams;

    console.log('Generating new networking');
    this.network = Network.generate(
        this.rng,
        this.state.degree,
        this.state.immunityFraction,
        this.state.networkSize,
        this.state.networkType,
    );

    // Run infection
    if (!this.props.reinfectionsAllowed && this.props.numAutoRuns > 0) {
      this.simulateInfections(this.props.numAutoRuns);
    }
  }

  simulateInfections(numSimulations: number) {
    for (let i = 0; i < numSimulations; i++) {
      this.simulateInfection();
    }
  }

  simulateInfection() {
    // Reset infection to patient zero
    for (let node of this.network.nodes) {
      node.infected = node.patientZero;
    }

    // Create frontier set
    let frontier = new Set();
    for (let node of this.network.getInfectedNodes()) {
      frontier.add(node);
    }

    let iterations = 0;
    while (frontier.size > 0 && (this.state.maxIterations === -1 || iterations < this.state.maxIterations)) {
      let nodesToSpread = frontier;
      frontier = new Set();

      for (let node of nodesToSpread) {
        for (let neighbor of node.neighbors) {
          if (neighbor.immune || neighbor.infected) {
            continue;
          }

          let infect = this.rng.random() < this.state.transmissionProbability;
          if (infect) {
            neighbor.infected = true;
            frontier.add(neighbor);
          }
        }
      }

      iterations++;
    }

    for (let node of this.network.nodes) {
      node.infectionHistory.push(node.infected);
    }
    console.log('finished simulating');
  }

  simulateStep() {
    // Create frontier set
    let currentInfectedNodes = new Set();
    for (let node of this.network.getInfectedNodes()) {
      currentInfectedNodes.add(node);
      node.infected = false;
    }

    for (let node of currentInfectedNodes) {
      let neighborsPlusSelf = Array.from(node.neighbors);
      neighborsPlusSelf.push(node);
      for (let neighbor of neighborsPlusSelf) {
        if (neighbor.immune || neighbor.infected) {
          continue;
        }

        let infect = this.rng.random() < this.state.transmissionProbability;
        if (infect) {
          neighbor.infected = true;
        }
      }
    }

    for (let node of this.network.nodes) {
      node.infectionHistory.push(node.infected);
    }
  }

  regenerate() {
    this.generate();
    this.forceUpdate();
  }

  togglePlayback() {
    this.setState({
      playing: !this.state.playing,
    });
  }

  redraw(force: boolean) {
    if (this.svg === null || this.svg === undefined) {
      return;
    }

    // actually redraw iff any of the drawing parameters have changed
    let currentDrawingParams = [
        this.network,
        this.state.drawNodeOutlines,
        this.state.drawRelationships,
    ];
    if (!force && Utils.arraysEqual(this.previousDrawingParams, currentDrawingParams)) {
      return;
    }
    this.previousDrawingParams = currentDrawingParams;


    console.log('redrawing...');

    this.svg.selectAll("svg > *").remove();

    this.background = this.svg.append('g');
    // this.background
    //   .append("rect")
    //   .style("stroke", 'none')
    //   .attr("fill", Widget.BACKGROUND_COLOR)
    //   .attr("x", 0)
    //   .attr("y", 0)
    //   .attr("width", this.width)
    //   .attr("height", this.height);
    this.relationships = this.svg.append('g');
    this.nodes = this.svg.append('g');


    for (let node of this.network.nodes) {
      let x = node.centerX * this.props.width;
      let y = node.centerY * this.props.height;
      let w = this.state.nodeSize;

      let fillColorHex = '#FFF';
      let fillOpacity = 1;
      if (this.props.reinfectionsAllowed) {
        fillColorHex = node.infected ? '#48F' : '#FFF';
      } else {
        if (node.infectionHistory.length > 1) {
          let infections = 0;
          for (let infection of node.infectionHistory) {
            if (infection) {
              infections++;
            }
          }
          let infectionFraction = infections / node.infectionHistory.length;
          fillColorHex = Colors.hex(Colors.blend(Colors.makeHex('#FFF'), Colors.makeHex('#48F'), infectionFraction));
        } else if (node.infected) {
          fillColorHex = '#48F';
        }

        // if (node.patientZero) {
        //   fillColorHex = '#F00';
        // }
      }
      let strokeColorHex = '#000';

      if (node.immune) {
        strokeColorHex = '#CCC';
        fillColorHex = '#CCC';
      }

      let nodeType = 'circle';
      if (this.state.nodeSize <= 10) {
        nodeType = 'rect';
      }

      this.drawNodeBody(nodeType, x, y, w, fillColorHex, fillOpacity);
      if (node.immune || !node.immune) {
        if (this.state.drawNodeOutlines) {
          this.drawNodeOutline(nodeType, x, y, w, strokeColorHex);
        }
      }

      if (this.state.drawRelationships) {
        for (let neighbor of node.neighbors) {
          let x2 = neighbor.centerX * this.props.width;
          let y2 = neighbor.centerY * this.props.height;

          let strokeWidth = 5;
          let strokeColor = '#ccc';
          if (node.highlighted) {
            strokeWidth = 5;
            strokeColor = '#000';
          }

          this.relationships
              .append('line')
              .style('stroke', strokeColor)
              .attr('stroke-width', strokeWidth)
              .attr('x1', x)
              .attr('y1', y)
              .attr('x2', x2)
              .attr('y2', y2);
        }
      }
    }
  }

  drawNodeOutline(nodeType: string, x, y, w, strokeColorHex) {
    let strokeWidth = Math.ceil(w / 10);

    if (nodeType === 'circle') {
      this.nodes
      .append('circle')
      .attr('fill', 'none')
      .attr("stroke", strokeColorHex)
      .attr("stroke-width", strokeWidth)
      .attr('cx', x)
      .attr('cy', y)
      .attr("r", w / 2);
    } else {
      this.nodes
      .append('rect')
      .style('fill', 'none')
      .style("stroke", strokeColorHex)
      .style("stroke-width", strokeWidth)
      .attr('x', x - w/2)
      .attr('y', y - w/2)
      .attr('width', w)
      .attr('height', w);
    }
  }

  drawNodeBody(nodeType: string, x, y, w, fillColorHex, fillOpacity) {
    if (nodeType === 'circle') {
      this.nodes
      .append('circle')
      .style("fill", fillColorHex)
      .style('fill-opacity', fillOpacity)
      .attr('cx', x)
      .attr('cy', y)
      .attr("r", w / 2);
    } else {
      this.nodes
      .append('rect')
      .style('fill', fillColorHex)
      .style('fill-opacity', fillOpacity)
      .attr('x', x - w/2)
      .attr('y', y - w/2)
      .attr('width', w)
      .attr('height', w);
    }
  }

  render() {
    this.generate();
    this.redraw();

    let transmissionProbabilitySlider = null;
    if (this.props.showTransmissionProbabilitySlider) {
      transmissionProbabilitySlider = <div>
        Transmission probability: <br/><br/><Slider style={{width: 200}}
                               min={0}
                               max={1}
                               value={this.state.transmissionProbability}
                               onChange={(e, value) => {
                                 this.setState({transmissionProbability: value});
                               }}/><br/>
        {this.state.transmissionProbability}
      </div>;
    }

    let immunityFractionSlider = null;
    if (this.props.showImmunityFractionSlider) {
      immunityFractionSlider = <div>
        Immunity fraction: <br/><br/><Slider style={{width: 200}}
                               min={0}
                               max={1}
                               value={this.state.immunityFraction}
                               onChange={(e, value) => {
                                 this.setState({immunityFraction: value});
                               }}/><br/>
      </div>;
    }

    let drawRelationshipsCheckbox = null;
    if (this.props.showDrawRelationshipsCheckbox) {
      drawRelationshipsCheckbox = <div className="draw-death-checkbox">
        <label><input type="checkbox" checked={this.state.drawRelationships}
                        onChange={(e) => {this.setState({drawRelationships: e.target.checked});} }/> relationships</label>
      </div>;
    }

    let newNetworkButton = null;
    let add1Button = null;
    let add10Button = null;
    let add100Button = null;
    if (this.props.showSimulationButtons) {
      newNetworkButton = <div><span onClick={() => {this.generate(true); this.forceUpdate();} } >New network</span></div>;
      if (!this.props.reinfectionsAllowed) {
        add1Button = <div><span onClick={() => {this.simulateInfections(1); this.redraw(true);} } >1 simulations</span></div>;
        add10Button = <div><span onClick={() => {this.simulateInfections(10); this.redraw(true);} } >10 simulations</span></div>;
        add100Button = <div><span onClick={() => {this.simulateInfections(100); this.redraw(true);} } >100 simulations</span></div>;
      }
    }

    let togglePlaybackButton = null;
    if (this.props.reinfectionsAllowed) {
      let text = 'Start!';
      if (this.state.playing) {
        text = 'Stop!';
      }
      togglePlaybackButton = <div><span onClick={() => {this.togglePlayback(); } } >{text}</span></div>;
    }

    return (
        <div className="widget-container">
          {transmissionProbabilitySlider}
          {immunityFractionSlider}
          {newNetworkButton}
          {add1Button}
          {add10Button}
          {add100Button}
          {togglePlaybackButton}
          <svg ref={this.myRef} width={this.width} height={this.height} />
          {drawRelationshipsCheckbox}
        </div>
    )
  }
}